import { AnalysisConfig } from "../models/AnalysisConfig";
import { AnalysisConfigField } from "../models/AnalysisConfigField";
import { AnalysisConfigFieldType } from "../models/AnalysisConfigFieldType";
import { AnalysisConfigSchema } from "../models/AnalysisConfigSchema";
import { AnalysisConfigTypeGuardService } from "./AnalysisConfigTypeGuardService";
import { QueueNCAAnalysisArgs } from "@/shared/Executions/models/QueueNCAAnalysisArgs";
import { QueueExecutionArgs } from "@/shared/Executions/models/QueueExecutionArgs";
import { IQueueExecutionRequest } from "@/shared/Executions/models/IQueueExecutionRequest";
import { QueueExecutionRequestFactory } from "@/shared/Executions/services/QueueExecutionRequestFactory";


export class AnalysisConfigService {

    public static readonly CUSTOM_ANALYSISCONFIG_TYPE = "unknown";


    public static getFlattenedAnalysisConfig(analysisConfig: AnalysisConfig, includeType: string | null): AnalysisConfig {
        if (analysisConfig == null) throw new Error("analysisConfig is required");

        const analysisConfigObject = analysisConfig.configuration ?? analysisConfig;
        const { _type, ...properties } = analysisConfigObject || {};
        const flattened = { ...properties } as AnalysisConfig;
        // Only include type data if at the top-level or configuration is a a known config type
        if (includeType) {
            flattened.type = includeType
        }
        return flattened;

    }

    public static createAnalysisConfigFromSchema(schema: AnalysisConfigSchema): AnalysisConfig {

        const newConfig: AnalysisConfig = {
            id: this.generateAnalysisConfigId(),
            _type: schema.type,
            display_name: schema.display_name,
            description: 'New analysis configuration',
            category: (schema as any).category ?? undefined,
            configuration: {},
            schema: schema
        }

        return newConfig;

    }

    public static createAnalysisConfigFromField(field: AnalysisConfigField): AnalysisConfig {

        const newConfig: AnalysisConfig = {
            id: this.generateAnalysisConfigId(),
            _type: field.field_type,
            display_name: field.display_name,
            display_field: field.display_field,
            description: field.description,
            category: "Default Value",
            configuration: {},
            schema: field
        }

        return newConfig;

    }

    private static generateAnalysisConfigId(): string {
        const id = `analysisconfig-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        return id;
    }

    public static initializeAnalysisConfigConfiguration(analysisConfig: AnalysisConfig, fields: Array<AnalysisConfigField>): Record<string, any> {
        const config: Record<string, any> = {}
        config.description = `${analysisConfig.display_name ?? "AnalysisConfig"} Name`
        fields.forEach((field: AnalysisConfigField) => {
            this.initializeAnalysisConfigConfigurationForField(field, config);
        })
        config._type = analysisConfig._type
        return config;
    }

    public static initializeAnalysisConfigFieldConfiguration(fields: Array<AnalysisConfigField>): Record<string, any> {
        const config: Record<string, any> = {}
        fields.forEach((field: AnalysisConfigField) => {
            this.initializeAnalysisConfigConfigurationForField(field, config);
        })
        return config;
    }

    public static initializeAnalysisConfigConfigurationForField(field: AnalysisConfigField, configuration?: Record<string, any>): Record<string, any> {
        const config: Record<string, any> = configuration || {};
        if (field.default_value !== undefined) {
            config[field.name] = field.default_value
        } else if (field.field_type === AnalysisConfigFieldType.Boolean) {
            config[field.name] = false
        } else if (
            field.field_type === AnalysisConfigFieldType.MultiSelect ||
            field.field_type === AnalysisConfigFieldType.ColumnMultiSelect ||
            field.field_type === AnalysisConfigFieldType.Array ||
            field.field_type === AnalysisConfigFieldType.ArrayValues ||
            field.field_type === AnalysisConfigFieldType.ColumnMultiSelectOrCreate
        ) {
            config[field.name] = []
        } else if (
            field.field_type === AnalysisConfigFieldType.Object ||
            field.field_type === AnalysisConfigFieldType.StringTemplate
        ) {
            config[field.name] = {}
        } else {
            config[field.name] = ''
        }
        return config;
    }

    public static createQueueNCAAnalysisArgs(
        fileId: string,
        flattenedAnalysisConfigs: Array<AnalysisConfig>,
        name?: string): QueueNCAAnalysisArgs {

        // Only use first config, even though standalone designer technically supports an array of configs
        let flattenedAnalysisConfig: AnalysisConfig | any = {};
        if (flattenedAnalysisConfigs?.length) {
            flattenedAnalysisConfig = flattenedAnalysisConfigs[0];
        }

        const args = new QueueNCAAnalysisArgs(
            flattenedAnalysisConfig,
            fileId,
            name ?? flattenedAnalysisConfig.description
        )

        return args
    }

    public static getFormattedAnalysisConfigsJson(fileId: string, analysisConfigs: Array<AnalysisConfig>): IQueueExecutionRequest {

        const flattened = this.getFlattendAnalysisConfigs(analysisConfigs);

        const args = AnalysisConfigService.createQueueNCAAnalysisArgs(fileId, flattened);
        const queueExecutionArgs = new QueueExecutionArgs({ tenantId: '', userId: '', ncaAnalysisArgs: args });
        const payload = QueueExecutionRequestFactory.createQueueExecutionRequest(queueExecutionArgs);
        return payload;
    }

    public static getFlattendAnalysisConfigs(analysisConfigs: Array<AnalysisConfig>): Array<AnalysisConfig> {

        const flattened = (analysisConfigs || []).map((x) => AnalysisConfigService.getFlattenedAnalysisConfig(x, x._type));
        this.recursivelyFlattenAnalysisConfigs(flattened);
        return flattened;

    }

    private static recursivelyFlattenAnalysisConfigs(items: Array<AnalysisConfig | Object>, visited = new Set<any>()) {
        for (const item of items) {
            if (visited.has(item)) continue;
            visited.add(item);

            for (const key of Object.keys(item)) {
                const value = (item as any)[key];

                if (Array.isArray(value)) {
                    const containsSubConfigs =
                        value.length > 0 &&
                        value.every((entry) => entry && AnalysisConfigTypeGuardService.isAnalysisConfig(entry));

                    if (containsSubConfigs) {
                        const flattenedSub = value.map((sub: AnalysisConfig) => {
                            const includeType = !this.isInternalSchemaType(sub._type) ? sub._type : null;
                            return this.getFlattenedAnalysisConfig(sub, includeType);
                        });

                        (item as any)[key] = flattenedSub;
                        this.recursivelyFlattenAnalysisConfigs(flattenedSub, visited);
                    }
                }
                else if (AnalysisConfigTypeGuardService.isNonEmptyAnalysisConfigObject(value)) {

                    for (const nestedKey of Object.keys(value)) {
                        const nestedValue = value[nestedKey];

                        // Case 1: Direct analysis config object
                        if (AnalysisConfigTypeGuardService.isAnalysisConfig(nestedValue)) {
                            const includeType = !this.isInternalSchemaType((nestedValue as AnalysisConfig)._type) ? (nestedValue as AnalysisConfig)._type : null;
                            const flattened = this.getFlattenedAnalysisConfig(nestedValue as AnalysisConfig, includeType);
                            value[nestedKey] = flattened;

                            this.recursivelyFlattenAnalysisConfigs([flattened], visited);
                        }

                        // Case 2: Another nested non-empty analysis-config-like object
                        else if (AnalysisConfigTypeGuardService.isNonEmptyAnalysisConfigObject(nestedValue)) {
                            const coerced = nestedValue as Object;
                            this.recursivelyFlattenAnalysisConfigs([coerced], visited);
                        }

                        else if (Array.isArray(nestedValue)) {
                            const configsArr = this.extractAnalysisConfigsFromArray(nestedValue);
                            if (configsArr.length > 0) {
                                const flattened = configsArr.map(sub => {
                                    const includeType = !this.isInternalSchemaType(sub._type) ? sub._type : null;
                                    return this.getFlattenedAnalysisConfig(sub, includeType);
                                });
                                value[nestedKey] = flattened;
                                this.recursivelyFlattenAnalysisConfigs(flattened, visited);
                            }
                        }
                    }
                }

            }
        }
    }

    private static extractAnalysisConfigsFromArray(arr: unknown[]): AnalysisConfig[] {
        return arr.filter(AnalysisConfigTypeGuardService.isAnalysisConfig) as AnalysisConfig[];
    }

    public static getFormattedAnalysisConfigsJsonString(fileId: string, analysisConfigs: Array<AnalysisConfig>, configsOnly: boolean = false): string {

        const payload = AnalysisConfigService.getFormattedAnalysisConfigsJson(fileId, analysisConfigs);
        return JSON.stringify((configsOnly ? payload.calculation : payload), null, 2)

    }

    public static tryGetAnalysisConfigSchemaByType(type: string, schemas: Array<AnalysisConfigSchema>): AnalysisConfigSchema | undefined {

        if (type == null) throw new Error("type is required");
        if (schemas == null) throw new Error("schemas are required");

        return schemas.find(x => x.type === type) || schemas.find(x => x.type === AnalysisConfigService.CUSTOM_ANALYSISCONFIG_TYPE);

    }

    // Certain item_schema structures use types like 'object' or 'array' that we do not want
    // persisted in output JSON.
    public static isInternalSchemaType(type: string): boolean {

        const internalTypes = ['object', 'array'];
        return internalTypes.indexOf(type?.toLowerCase().trim()) !== -1;

    }


}
